// SPDX-License-Identifier: AGPL-3.0-or-later

import MessageFocus from '@app/features/messaging/state/MessageFocus';
import type {ScrollerHandle} from '@app/features/ui/components/Scroller';
import KeyboardMode from '@app/features/ui/state/KeyboardMode';
import {type RefObject, useEffect, useRef} from 'react';

interface MessageListKeyboardNavigationOptions {
	containerRef?: RefObject<ScrollerHandle | HTMLElement | null>;
	channelId?: string;
	onFocusMessage?: (messageId: string) => void;
	onLoadMoreBefore?: () => void;
	onLoadMoreAfter?: () => void;
	hasMoreBefore?: boolean;
	hasMoreAfter?: boolean;
	isLoadingMore?: boolean;
	onEscape?: () => void;
	allowWhenInactive?: boolean;
}
const getScrollerNode = (value: ScrollerHandle | HTMLElement | null | undefined): HTMLElement | null => {
	if (!value) return null;
	if ('getScrollerNode' in value && typeof value.getScrollerNode === 'function') {
		return value.getScrollerNode();
	}
	if (value instanceof HTMLElement) {
		return value;
	}
	return null;
};
const isEditableTarget = (target: Element | null): boolean => {
	if (!target) return false;
	if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) return true;
	return target instanceof HTMLElement && target.isContentEditable;
};
const hasShortcutModifier = (event: KeyboardEvent): boolean =>
	event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
export function useMessageListKeyboardNavigation(options: MessageListKeyboardNavigationOptions): void {
	const {
		containerRef,
		channelId,
		onFocusMessage,
		onLoadMoreBefore,
		onLoadMoreAfter,
		hasMoreBefore = false,
		hasMoreAfter = false,
		isLoadingMore = false,
		onEscape,
		allowWhenInactive = false,
	} = options;
	const messageNodesCache = useRef({nodes: [] as Array<HTMLElement>, ts: 0});
	const keyboardModeEnabled = KeyboardMode.keyboardModeEnabled;
	useEffect(() => {
		if (!keyboardModeEnabled) return;
		const getMessageElements = (): Array<HTMLElement> => {
			const now = Date.now();
			const cache = messageNodesCache.current;
			if (cache.nodes.length && now - cache.ts < 120) {
				return cache.nodes;
			}
			const container = getScrollerNode(containerRef?.current ?? null);
			if (!container && containerRef) {
				cache.nodes = [];
				cache.ts = now;
				return cache.nodes;
			}
			const root = container ?? document;
			const selector = channelId
				? `[data-channel-id="${channelId}"][data-message-id]`
				: '[data-message-id][data-channel-id]';
			const nodes = Array.from(root.querySelectorAll<HTMLElement>(selector));
			cache.nodes = nodes;
			cache.ts = now;
			return nodes;
		};
		const focusNode = (node: HTMLElement, messageId: string) => {
			if (onFocusMessage) {
				onFocusMessage(messageId);
				return;
			}
			node.focus({preventScroll: true});
			const container = getScrollerNode(containerRef?.current ?? null);
			if (container) {
				const containerRect = container.getBoundingClientRect();
				const nodeRect = node.getBoundingClientRect();
				if (nodeRect.top < containerRect.top || nodeRect.bottom > containerRect.bottom) {
					node.scrollIntoView({block: 'nearest', inline: 'nearest'});
				}
			} else {
				node.scrollIntoView({block: 'nearest', inline: 'nearest'});
			}
		};
		const focusByDelta = (delta: number) => {
			const nodes = getMessageElements();
			if (!nodes.length) return;
			const activeId = MessageFocus.focusedMessageId ?? null;
			let idx = nodes.findIndex((node) => node.dataset.messageId === activeId);
			if (idx === -1) {
				idx = delta > 0 ? -1 : nodes.length;
			}
			const nextIdx = idx + delta;
			if (nextIdx < 0) {
				if (hasMoreBefore && onLoadMoreBefore && !isLoadingMore) {
					onLoadMoreBefore();
				}
				return;
			}
			if (nextIdx >= nodes.length) {
				if (hasMoreAfter && onLoadMoreAfter && !isLoadingMore) {
					onLoadMoreAfter();
				}
				return;
			}
			const nextNode = nodes[nextIdx];
			const nextId = nextNode?.dataset?.messageId;
			if (nextId) {
				focusNode(nextNode, nextId);
			}
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!keyboardModeEnabled) return;
			if (isEditableTarget(document.activeElement)) return;
			const container = getScrollerNode(containerRef?.current ?? null);
			if (container) {
				const activeElement = container.ownerDocument?.activeElement ?? document.activeElement;
				const activeInContainer = activeElement ? container.contains(activeElement) : false;
				const focusedId = MessageFocus.focusedMessageId;
				const focusedInContainer =
					focusedId != null && getMessageElements().some((node) => node.dataset.messageId === focusedId);
				const canHandleWhenInactive =
					allowWhenInactive &&
					(activeElement === document.body || activeElement === document.documentElement || !activeElement);
				if (!activeInContainer && !focusedInContainer && !canHandleWhenInactive) {
					return;
				}
			}
			switch (event.key) {
				case 'ArrowUp':
					if (hasShortcutModifier(event)) return;
					event.preventDefault();
					focusByDelta(-1);
					return;
				case 'ArrowDown':
					if (hasShortcutModifier(event)) return;
					event.preventDefault();
					focusByDelta(1);
					return;
				case 'Escape':
					if (onEscape) {
						event.preventDefault();
						onEscape();
					}
					return;
				default:
					return;
			}
		};
		window.addEventListener('keydown', handleKeyDown, true);
		return () => {
			window.removeEventListener('keydown', handleKeyDown, true);
		};
	}, [
		keyboardModeEnabled,
		containerRef,
		channelId,
		onFocusMessage,
		onLoadMoreBefore,
		onLoadMoreAfter,
		hasMoreBefore,
		hasMoreAfter,
		isLoadingMore,
		onEscape,
		allowWhenInactive,
	]);
}
